import { createSignal } from "solid-js";

import { closestSelector } from "@/lib/gestures/guards";
import { applyRubberBand, createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";
import { TODO_ROW_SWIPE } from "@/lib/gestures/presets";

interface EntrySwipeGesturesOptions {
  /** Whether this row supports a left-swipe action (e.g. delete / dismiss). */
  canSwipeLeft: () => boolean;
  /** Whether this row supports a right-swipe action (e.g. edit). */
  canSwipeRight: () => boolean;
  /** Committed left-swipe (drag past threshold to the left). */
  onSwipeLeft: () => void;
  /** Committed right-swipe (drag past threshold to the right). */
  onSwipeRight: () => void;
  /** The sliding surface, used to measure width for the commit slide-out. */
  getSurfaceRef: () => HTMLElement | undefined;
}

const {
  thresholdPx: THRESHOLD_PX,
  commitThresholdPx: COMMIT_THRESHOLD_PX,
  rubberBandLimitPx: RUBBER_BAND_LIMIT,
  rubberBandFactor: RUBBER_BAND_FACTOR,
  hapticMs: HAPTIC_VIBRATION_MS,
  commitAnimationMs: COMMIT_ANIMATION_MS,
  cancelAnimationMs: CANCEL_ANIMATION_MS,
  fallbackWidthPx: FALLBACK_WIDTH_PX,
} = TODO_ROW_SWIPE;

// oxlint-disable-next-line lingui/no-unlocalized-strings -- CSS media query, not UI copy
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false;
}

/**
 * Per-row swipe-to-action for ChronosTracker timeline rows. Mirrors the template's
 * `useTodoSwipeGestures`: same recognizer, preset, haptics and rubber-band, but
 * the committed actions are injected so a row can opt into left only (dismiss),
 * right only, or both (registered entries: right=edit, left=delete).
 *
 * Honours `prefers-reduced-motion` by committing the action immediately instead
 * of animating the row off-screen. The committed action itself stays untouched,
 * so keyboard/button paths (Edit/Delete/Dismiss) remain the source of truth.
 */
export function useEntrySwipeGestures(options: EntrySwipeGesturesOptions) {
  const [tx, setTx] = createSignal(0);
  const [isSwiping, setIsSwiping] = createSignal(false);
  const [isAnimating, setIsAnimating] = createSignal(false);

  let hasSwiped = false;

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: THRESHOLD_PX,
    commitPx: COMMIT_THRESHOLD_PX,
    haptics: true,
    hapticMs: HAPTIC_VIBRATION_MS,
    canStart: ({ target }) => !closestSelector(target, 'button, a, [data-no-entry-swipe="true"]'),
    onStart: () => {
      setIsSwiping(true);
      setIsAnimating(false);
    },
    onMove: (sample) => {
      // Clamp to the directions this row actually supports so an unsupported
      // drag rubber-bands at zero rather than revealing a phantom action.
      let { dx } = sample;
      if (dx < 0 && !options.canSwipeLeft()) {
        dx = 0;
      }
      if (dx > 0 && !options.canSwipeRight()) {
        dx = 0;
      }
      setTx(applyRubberBand(dx, RUBBER_BAND_LIMIT, RUBBER_BAND_FACTOR));
    },
    onEnd: () => {
      hasSwiped = true;
      setIsSwiping(false);

      const finalTx = tx();
      const surfaceRef = options.getSurfaceRef();
      const width = surfaceRef?.clientWidth ?? FALLBACK_WIDTH_PX;
      const reduceMotion = prefersReducedMotion();

      const commitRight = finalTx > COMMIT_THRESHOLD_PX && options.canSwipeRight();
      const commitLeft = finalTx < -COMMIT_THRESHOLD_PX && options.canSwipeLeft();

      if (commitRight || commitLeft) {
        const action = commitRight ? options.onSwipeRight : options.onSwipeLeft;
        if (reduceMotion) {
          // No slide-out: snap back and fire immediately.
          setTx(0);
          setIsAnimating(false);
          action();
          return;
        }
        setIsAnimating(true);
        setTx(commitRight ? width : -width);
        setTimeout(() => {
          action();
          setTx(0);
          setIsAnimating(false);
        }, COMMIT_ANIMATION_MS);
        return;
      }

      // Below threshold (or unsupported direction): bounce back to center.
      setIsAnimating(true);
      setTx(0);
      setTimeout(() => {
        setIsAnimating(false);
      }, CANCEL_ANIMATION_MS);
    },
    onCancel: () => {
      setIsSwiping(false);
      setIsAnimating(true);
      setTx(0);
      setTimeout(() => {
        setIsAnimating(false);
      }, CANCEL_ANIMATION_MS);
    },
  });

  const base = pointerHandlers(recognizer);

  return {
    tx,
    isSwiping,
    isAnimating,
    handlePointerDown: base.onPointerDown,
    handlePointerMove: base.onPointerMove,
    handlePointerUp: base.onPointerUp,
    handlePointerCancel: base.onPointerCancel,
    getHasSwiped: () => hasSwiped,
    resetHasSwiped: () => {
      hasSwiped = false;
    },
    COMMIT_THRESHOLD_PX,
  };
}
